import React, { useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DropdownMenu } from "components/common/DropdownMenu";
import { TextInput } from "components/common/TextInput";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  flattenSpaceTree,
  spaceColor,
  useOrgContextStore,
  visibleSpaces,
} from "@/components/main/stores/orgContextStore";

interface SpacePickerMenuProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  selectedSpaceId: string | null;
  onPick: (spaceId: string) => void;
  title?: string;
}

/// The "CREATE IN" space picker (S6d, per the approved mocks): a filterable
/// FLAT list with left-truncated ancestor paths — never an indented tree.
/// Ancestors render muted, the leaf bright; sealed spaces carry the lock. A
/// space inside its deletion window is not offered: it is in the Trash (D52).
export const SpacePickerMenu: React.FC<SpacePickerMenuProps> = ({
  anchorRef,
  isOpen,
  onClose,
  selectedSpaceId,
  onPick,
  title = "Create in",
}) => {
  const [query, setQuery] = useState("");
  const spaces = useOrgContextStore((s) => s.spaces);
  const paths = useMemo(
    () => flattenSpaceTree(visibleSpaces(spaces)),
    [spaces],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(({ space, ancestors }) =>
      [...ancestors, space.name].some((name) => name.toLowerCase().includes(q)),
    );
  }, [paths, query]);

  return (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={() => {
        setQuery("");
        onClose();
      }}
      placement="bottom-end"
      style={{ minWidth: "18.75rem" }}
    >
      <div className="flex flex-col p-1">
        <span
          className="px-2 py-1 font-mono text-fg-muted uppercase select-none"
          style={{
            fontSize: "var(--font-size-xx-small)",
            letterSpacing: "0.1em",
          }}
        >
          {title}
        </span>
        <TextInput
          value={query}
          onChange={setQuery}
          placeholder="Find a space…"
          autoFocus
          ariaLabel="Find a space"
          className="h-[var(--control-height-sm)] w-full rounded-app border border-border bg-bg-subtle px-2 text-small text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none"
        />
        <div className="mt-1 max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <span className="block px-2 py-2 text-fg-muted-regular-small">
              No spaces match
            </span>
          ) : (
            filtered.map(({ space, ancestors }) => {
              const isSelected = space.space_id === selectedSpaceId;
              return (
                <button
                  key={space.space_id}
                  onClick={() => {
                    onPick(space.space_id);
                    setQuery("");
                    onClose();
                  }}
                  className={`flex h-7 w-full min-w-0 items-center gap-2 rounded-app px-2 text-left ${
                    isSelected
                      ? "bg-list-selected-light"
                      : "hover:bg-list-hover"
                  }`}
                >
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-app"
                    style={{
                      backgroundColor: resolveDatasetColor(spaceColor(space)),
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {ancestors.map((name) => (
                      <span
                        key={name}
                        className="text-small text-fg-muted"
                      >{`${name} / `}</span>
                    ))}
                    <span className="font-medium text-fg">{space.name}</span>
                  </span>
                  {space.sealed && (
                    <span
                      className="flex shrink-0 items-center gap-1 font-mono text-fg-muted uppercase"
                      style={{
                        fontSize: "var(--font-size-xx-small)",
                        letterSpacing: "0.05em",
                      }}
                    >
                      <LockOutlinedIcon sx={{ fontSize: 11 }} />
                      Sealed
                    </span>
                  )}
                  {isSelected && (
                    <CheckIcon
                      sx={{ fontSize: 14 }}
                      className="shrink-0 text-accent"
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </DropdownMenu>
  );
};
